import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  FunctionComponent,
  useEffect,
  useRef
} from 'react';
import { useClient } from '@/hooks/useClient';
import { useAuth } from './auth';

interface PinStatusResponse {
  is_configured: boolean;
  is_enabled: boolean;
  pin_length?: number;
  is_locked?: boolean;
  locked_until?: string;
}

interface VerifyPinResponse {
  success: boolean;
  remaining_attempts?: number;
  locked_until?: string;
  is_locked?: boolean;
  message?: string;
}

const PIN_LENGTH = 4;

interface PinProtectionContextI {
  isPinConfigured: boolean;
  isPinEnabled: boolean;
  pinLength: number;
  isLocked: boolean;
  lockedUntil: Date | null;
  isVerified: boolean;
  showPinModal: boolean;
  pendingRoute: string | null;
  isLoading: boolean;
  setShowPinModal: (show: boolean) => void;
  setPendingRoute: (route: string | null) => void;
  verifyPin: (pin: string) => Promise<{ success: boolean; remaining_attempts?: number; locked_until?: Date }>;
  refreshStatus: () => Promise<void>;
  resetVerification: () => void;
}

const PinProtectionContext = createContext<PinProtectionContextI | undefined>(undefined);

interface PinProtectionProviderPropsI {
  children?: React.ReactNode;
}

export const PinProtectionProvider: FunctionComponent<PinProtectionProviderPropsI> = ({ children }) => {
  const { client } = useClient();
  const { isAuthenticated } = useAuth();
  const [isPinConfigured, setIsPinConfigured] = useState(false);
  const [isPinEnabled, setIsPinEnabled] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [lockedUntil, setLockedUntil] = useState<Date | null>(null);
  const [isVerified, setIsVerified] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pendingRoute, setPendingRoute] = useState<string | null>(null);
  // Arranca EN CARGA: hasta que el estado del PIN llegó, las rutas protegidas no
  // pintan contenido (PinProtectedRoute muestra el esqueleto). Antes arrancaba en
  // false y el contenido aparecía, se iba a blanco y recién ahí salía el modal.
  const [isLoading, setIsLoading] = useState(true);
  const hasFetchedRef = useRef(false);

  const isGodMode = !!localStorage.getItem('vendorUsernameGodMode');

  // Al cerrar sesión, la próxima vuelve a esperar el estado antes de pintar (ajuste de
  // estado durante el render, sin efecto: no hay cuadro intermedio con isLoading=false).
  const [wasAuthenticated, setWasAuthenticated] = useState(isAuthenticated);
  if (wasAuthenticated !== isAuthenticated) {
    setWasAuthenticated(isAuthenticated);
    if (!isAuthenticated) setIsLoading(true);
  }

  const refreshStatus = useCallback(async () => {
    if (!isAuthenticated) return;

    // Skip PIN for god mode (admin impersonation)
    if (isGodMode) {
      setIsPinConfigured(false);
      setIsPinEnabled(false);
      setIsVerified(true);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await client<PinStatusResponse>('me/vendor/security-pin/status', 'GET', {
        isAuthRequired: true
      });

      setIsPinConfigured(response.is_configured);
      setIsPinEnabled(response.is_enabled);
      setIsLocked(response.is_locked || false);
      setLockedUntil(response.locked_until ? new Date(response.locked_until) : null);
    } catch (error) {
      console.error('Failed to fetch PIN status', error);
    } finally {
      setIsLoading(false);
    }
  }, [client, isAuthenticated, isGodMode]);

  const verifyPin = useCallback(
    async (pin: string) => {
      try {
        const response = await client<VerifyPinResponse>('me/vendor/security-pin/verify', 'POST', {
          isAuthRequired: true,
          data: { pin }
        });

        if (response.success) {
          setIsVerified(true);
          setIsLocked(false);
          setLockedUntil(null);
          return { success: true };
        }

        // Handle locked response (423 returns as success with is_locked)
        if (response.is_locked && response.locked_until) {
          setIsLocked(true);
          setLockedUntil(new Date(response.locked_until));
          return { success: false, locked_until: new Date(response.locked_until) };
        }

        // Incorrect PIN (200 with success: false)
        return {
          success: false,
          remaining_attempts: response.remaining_attempts
        };
      } catch (error: any) {
        // Handle 423 locked response (may come as error depending on client)
        const errorData = error?.response || error;
        if (errorData?.is_locked && errorData?.locked_until) {
          setIsLocked(true);
          setLockedUntil(new Date(errorData.locked_until));
          return { success: false, locked_until: new Date(errorData.locked_until) };
        }
        return { success: false };
      }
    },
    [client]
  );

  const resetVerification = useCallback(() => {
    setIsVerified(false);
  }, []);

  // Fetch status once when authenticated
  useEffect(() => {
    if (isAuthenticated && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      refreshStatus();
    }
    if (!isAuthenticated) {
      hasFetchedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return (
    <PinProtectionContext
      value={{
        isPinConfigured,
        isPinEnabled,
        pinLength: PIN_LENGTH,
        isLocked,
        lockedUntil,
        isVerified,
        showPinModal,
        pendingRoute,
        isLoading,
        setShowPinModal,
        setPendingRoute,
        verifyPin,
        refreshStatus,
        resetVerification
      }}
    >
      {children}
    </PinProtectionContext>
  );
};

export const usePinProtection = (): PinProtectionContextI => {
  const context = useContext(PinProtectionContext);
  if (context === undefined) {
    throw new Error('usePinProtection must be used within PinProtectionProvider');
  }
  return context;
};
